import "./CourseDetails.css";
import MainLayout from "../layouts/MainLayout/MainLayout";
import CourseRequirements from "../features/Course/CourseRequirements/CourseRequirements";
import CourseCertificationContainer from "../features/Course/CourseCertification/CourseCertificationContainer";
import CourseBenefits from "../features/Course/CourseBenefits/CourseBenefits";
import CourseTab from "../features/Course/CourseTab/CourseTab";
import CourseHero from "../features/Course/CourseHero/CourseHero";
import { useEffect, useRef, useState } from "react";
import CourseDetailsCard from "../features/Course/CourseDetailsCard/CourseDetailsCard";
import { IconReport, IconClock, IconCalendar, IconRocket } from "@tabler/icons-react";
import { Button, Skeleton } from "@mantine/core";
import axios from "axios";
import DecorativeCircle from "../components/DecorativeCircle/DecorativeCircle";
import TinyDecorativeCircle from "../components/TinyDecorativeCircle/TinyDecorativeCircle";
import { useNavigate, useParams } from 'react-router-dom'
import { useAppContext } from "../context/AppState/AppContext";
import { calculatePrice } from "../utils/calculatePrice";
import CourseCampusDemo from "../features/Course/CourseCampusDemo/CourseCampusDemo";
import { Helmet } from 'react-helmet'
import ContactAdviserModal from "../components/ContactAdviserModal/ContactAdviserModal";
import { ROUTES_PATHNAME } from "../routes/dict";

const featuresBuilder = (_cardInfo) => {
    return [
        {
            icon: <IconRocket className="cardFeature__icon" />,
            text: "Fecha de inicio",
            description: _cardInfo?.initialDate ? `${_cardInfo.initialDate}` : "A confirmar"
        },
        {
            icon: <IconReport className="cardFeature__icon" />,
            text: "Duración del programa",
            description: _cardInfo.classQuantity ? `${_cardInfo.classQuantity} Clases` : "A confirmar"
        },
        {
            icon: <IconClock className="cardFeature__icon" />,
            text: "Horario",
            description: _cardInfo.classHour ? `${_cardInfo.classHour} hs. (UTC-3)` : "A confirmar"
        },
        {
            icon: <IconCalendar className="cardFeature__icon" />,
            text: "Días",
            description: _cardInfo.classDays ? `${_cardInfo.classDays}` : "A confirmar"
        },
    ]
}

const MODAL_DELAY_IN_MS = 8500

export default function CourseDetails({ isGrand = false }) {
    const { setPaymentData, isUSD, isMobile, officialUSDValue } = useAppContext();
    const [academicUnitInfo, setAcademicUnitInfo] = useState(null);
    const { id } = useParams()
    const navigate = useNavigate();
    const hasShownModal = useRef(false);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const addViewToCurrentAcademicUnit = async () => {
            const URL = `${process.env.REACT_APP_BACKEND_SERVER}/info/addViewToAcademicUnit`
            const data = {
                academicUnitId: id
            }
            await axios.put(URL, data)
        }
        addViewToCurrentAcademicUnit()
    }, [])


    useEffect(() => {
        if (!hasShownModal.current) {
            hasShownModal.current = true;

            const showModal = () => {
                setShowModal(true);
                removeListeners(); // Evita mostrarlo más de una vez
            };

            const timer = setTimeout(showModal, MODAL_DELAY_IN_MS);

            const handleMouseLeave = (e) => {
                // Solo si el mouse sale por arriba o fuera de la ventana
                if (e.clientY <= 0 || e.relatedTarget === null) {
                    showModal();
                }
            };

            const handleVisibilityChange = () => {
                if (document.visibilityState === 'hidden') {
                    showModal();
                }
            };

            const removeListeners = () => {
                clearTimeout(timer);
                document.removeEventListener('mouseleave', handleMouseLeave);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };

            document.addEventListener('mouseleave', handleMouseLeave);
            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => {
                removeListeners();
            };
        }
    }, []);

    const handleInscription = () => {
        if (!isOpen) return
        setPaymentData({
            name: academicUnitInfo.name,
            id: academicUnitInfo.id,
            price,
            image: academicUnitInfo.image,
            division: academicUnitInfo.division || null,
            subscriptionPlans: academicUnitInfo.subscriptionPlans, // Only PayPal
        });
        const navigateTo = isGrand ? "/grand/pago" : "/pago"
        navigate(navigateTo)
    }

    useEffect(() => {
        const url = process.env.REACT_APP_BACKEND_SERVER + `/info/academicUnits/${id}`;
        axios.get(url)
            .then(response => {
                setAcademicUnitInfo(response.data)
            })
            .catch(err => console.log(err))
    }, [id])




    if (!academicUnitInfo) return <MainLayout><Skeleton height={400} radius="sm" /></MainLayout>

    const OPEN_AU_STRING = 'Open'
    const price = calculatePrice(academicUnitInfo.price, officialUSDValue, isUSD, academicUnitInfo.type);
    const isOpen = academicUnitInfo.state === OPEN_AU_STRING



    return (
        <>
            <Helmet>
                <title>{academicUnitInfo.name} | Forvet</title>
                <meta name="description" content={academicUnitInfo.description?.slice(0, 160)} />
                <meta property="og:title" content={academicUnitInfo.name} />
                <meta property="og:description" content={academicUnitInfo.description} />
                <meta property="og:image" content={academicUnitInfo.heroImage} />
                <meta property="og:url" content={`https://forvet.org${ROUTES_PATHNAME.courses.href}/${academicUnitInfo.id}`} />
            </Helmet>
            <MainLayout >
                <div className="course__layout">
                    <section className="course__hero" style={{ position: "relative" }}>
                        <CourseHero
                            isGrand={isGrand}
                            isOpen={isOpen}
                            title={academicUnitInfo.name}
                            description={academicUnitInfo.description}
                            imageUrl={academicUnitInfo.heroImage}
                        />
                        <DecorativeCircle size={100} top={-20} left={0} opacity={.1} shadow={1} />
                        <DecorativeCircle size={150} bottom={-100} left={300} opacity={.1} shadow={1} hideInMobile />
                        <DecorativeCircle size={50} top={-20} left={600} opacity={.1} shadow={4} hideInMobile />
                        <DecorativeCircle size={100} top={-50} right={0} opacity={.1} shadow={2} />
                        <TinyDecorativeCircle size={12} top={50} right={300} opacity={.1} color={1} />
                        <TinyDecorativeCircle size={12} top={300} left={70} opacity={.1} color={1} />
                        <TinyDecorativeCircle size={32} top={80} left={500} opacity={.1} color={3} hideInMobile />

                    </section>
                    <aside className="course__aside">
                        <CourseDetailsCard
                            isGrand={isGrand}
                            price={price}
                            videoUrl={academicUnitInfo?.video}
                            features={featuresBuilder(academicUnitInfo.cardInfo)}
                            handleInscription={handleInscription}
                            isOpen={isOpen}
                            // This is equivalent to state sent above on navigate(), beware if you change it
                            courseData={{
                                name: academicUnitInfo.name,
                                id: academicUnitInfo.id,
                            }}
                        />
                    </aside>

                    <div className="course__main">
                        <section className='course__section'>
                            <CourseTab
                                isGrand={isGrand}
                                specialtyId={Array.isArray(academicUnitInfo.specialties) ? academicUnitInfo.specialties[0] : academicUnitInfo.specialties}
                                modules={academicUnitInfo.modules}
                                staff={academicUnitInfo.staff}
                                reviews={academicUnitInfo.reviews}
                            />
                        </section>

                        <section className="course__section--noPadding">
                            <CourseRequirements isGrand={isGrand} text={academicUnitInfo.whoIsThisFor} />
                        </section>

                        <section className="course__section">
                            <CourseCertificationContainer isGrand={isGrand} certificate={academicUnitInfo.certificate} />
                        </section>

                        <section className="course__section">
                            <CourseBenefits isGrand={isGrand} />
                        </section>

                        <section className="course__section">
                            <CourseCampusDemo isGrand={isGrand} />
                        </section>

                        {!isOpen && !isMobile &&
                            <section className="course__section">
                                <span className="sectionTitle-upText">¡Inscripciones cerradas!</span>
                            </section>}
                    </div>
                </div>

                <div className={`course__buttons ${isGrand ? "course__buttons_grand" : ""}`}>
                    <Button className="courseDetailsCard__button" fw='normal' color={isGrand ? "var(--color-grand-accent)" : "secondaryColor"} disabled={!isOpen}
                        fullWidth onClick={() => handleInscription()}>
                        {isOpen ? 'Inscribirse' : 'Inscripciones cerradas'}
                    </Button>
                    <Button className="courseDetailsCard__button" fw='normal' variant={isGrand ? "outline" : "default"} fullWidth
                        onClick={() => navigate("/contacto", {
                            // This is equivalent to courseData, beware if you change it
                            state: {
                                name: academicUnitInfo.name,
                                id: academicUnitInfo.id,
                            }
                        })}>
                        Consultar un asesor
                    </Button>
                </div>
                {showModal && <ContactAdviserModal opened={showModal} onClose={() => setShowModal(false)} academicUnitId={id} academicUnitName={academicUnitInfo.name} />}
            </MainLayout>
        </>
    )
}
